import React from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { ALEGRA_SYNC_TEXTS } from "../shared/constants";
import { SyncJobType } from "../../types/types";

// Presentational. The job itself is owned by `useAlegraSync` in the page above, because the run
// outlives this dialog — closing it must not abandon a sync that is still writing.

const PendingCurationBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  borderLeft: `${theme.spacing(0.5)} solid ${theme.palette.primary.main}`,
  borderRadius: theme.spacing(1),
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const MetricGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
}));

const MetricBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  borderRadius: theme.spacing(1),
  padding: theme.spacing(1.5),
}));

const NoteText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  marginTop: theme.spacing(1),
}));

const Metric: React.FC<{ label: string; value: number }> = ({ label, value }) => (
  <MetricBox>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h6" fontWeight="bold" color="text.primary">
      {value.toLocaleString("es-CO")}
    </Typography>
  </MetricBox>
);

interface Props {
  open: boolean;
  itemCount: number;
  job: SyncJobType | undefined;
  isStarting: boolean;
  isRunning: boolean;
  isFinished: boolean;
  error: string | null;
  onConfirm: () => void;
  onClose: () => void;
}

const AlegraSyncDialog: React.FC<Props> = ({
  open,
  itemCount,
  job,
  isStarting,
  isRunning,
  isFinished,
  error,
  onConfirm,
  onClose,
}) => {
  const counters = job?.counters;
  const summary = job?.summary;
  const vanishedCount = summary?.vanished_count ?? 0;
  // Absent on job rows written before EB-549 — an admin can still open an old run's summary.
  const transitions = summary?.availability_transitions ?? [];
  const isDryRun = !!summary?.dry_run;

  // A dry run writes nothing, so every counter comes back zero while `planned` holds what the run
  // *would* have done. Reading the counters regardless would have the simulation report
  // "0 productos nuevos pendientes de curar" — the opposite of what it just computed.
  const createdCount = isDryRun ? (summary?.planned.creates ?? 0) : (counters?.created ?? 0);
  const updatedCount = isDryRun ? (summary?.planned.updates ?? 0) : (counters?.updated ?? 0);

  const title = isFinished
    ? ALEGRA_SYNC_TEXTS.summaryTitle
    : isRunning
      ? ALEGRA_SYNC_TEXTS.runningTitle
      : ALEGRA_SYNC_TEXTS.confirmTitle;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth data-testid="alegra-sync-dialog">
      <DialogTitle>{title}</DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity={isFinished ? "warning" : "error"} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!isRunning && !isFinished && (
          <>
            <Typography color="text.primary" gutterBottom>
              {ALEGRA_SYNC_TEXTS.confirmIntro}
            </Typography>
            <Typography color="text.primary" gutterBottom>
              {ALEGRA_SYNC_TEXTS.confirmScope}
            </Typography>
            {/* The one consequence an admin must not be surprised by: nothing new becomes visible. */}
            <Alert severity="info">{ALEGRA_SYNC_TEXTS.confirmInactive}</Alert>
            <NoteText variant="body2">
              {ALEGRA_SYNC_TEXTS.progressScanned(itemCount)} en el catálogo actual.
            </NoteText>
          </>
        )}

        {isRunning && (
          <>
            <Typography color="text.primary" gutterBottom>
              {ALEGRA_SYNC_TEXTS.statusLabels[job?.status ?? "queued"]}
            </Typography>
            <LinearProgress />
            {!!counters?.fetched && (
              <NoteText variant="body2">
                {ALEGRA_SYNC_TEXTS.progressFetched(counters.fetched)}
              </NoteText>
            )}
            <NoteText variant="body2">{ALEGRA_SYNC_TEXTS.runningHint}</NoteText>
          </>
        )}

        {isFinished && counters && (
          <>
            {summary?.dry_run && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {ALEGRA_SYNC_TEXTS.dryRunNotice}
              </Alert>
            )}

            {/* Leads the summary by design: synced products land inactive, so this backlog is the
                real outcome of a run, not a detail (plan §4.3.1). */}
            <PendingCurationBox>
              <Typography variant="h6" fontWeight="bold" color="text.primary">
                {isDryRun
                  ? ALEGRA_SYNC_TEXTS.pendingCurationDryRun(createdCount)
                  : ALEGRA_SYNC_TEXTS.pendingCuration(createdCount)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {ALEGRA_SYNC_TEXTS.pendingCurationHint}
              </Typography>
            </PendingCurationBox>

            <MetricGrid>
              <Metric
                label={
                  isDryRun ? ALEGRA_SYNC_TEXTS.labelCreatedDryRun : ALEGRA_SYNC_TEXTS.labelCreated
                }
                value={createdCount}
              />
              <Metric
                label={
                  isDryRun ? ALEGRA_SYNC_TEXTS.labelUpdatedDryRun : ALEGRA_SYNC_TEXTS.labelUpdated
                }
                value={updatedCount}
              />
              <Metric label={ALEGRA_SYNC_TEXTS.labelSkipped} value={counters.skipped} />
              <Metric label={ALEGRA_SYNC_TEXTS.labelFailed} value={counters.failed} />
              <Metric label={ALEGRA_SYNC_TEXTS.labelFetched} value={counters.fetched} />
              <Metric label={ALEGRA_SYNC_TEXTS.labelScanned} value={counters.scanned} />
            </MetricGrid>

            {transitions.length > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight="bold">
                  {ALEGRA_SYNC_TEXTS.transitionsTitle}
                </Typography>
                {transitions.map((transition) => (
                  <Typography variant="body2" key={`${transition.from}>${transition.to}`}>
                    {ALEGRA_SYNC_TEXTS.transitionLabels[`${transition.from}>${transition.to}`] ??
                      `${transition.from} → ${transition.to}`}
                    {": "}
                    {transition.count.toLocaleString("es-CO")}
                  </Typography>
                ))}
                <Typography variant="caption" color="text.secondary">
                  {ALEGRA_SYNC_TEXTS.transitionsHint}
                </Typography>
              </Alert>
            )}

            {vanishedCount > 0 && (
              <Alert severity="warning">
                <Typography variant="body2" fontWeight="bold">
                  {ALEGRA_SYNC_TEXTS.vanishedTitle(vanishedCount)}
                </Typography>
                <Typography variant="body2">{ALEGRA_SYNC_TEXTS.vanishedHint}</Typography>
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        {!isRunning && !isFinished && (
          <>
            <Button onClick={onClose} disabled={isStarting}>
              {ALEGRA_SYNC_TEXTS.cancelButton}
            </Button>
            <Button onClick={onConfirm} variant="contained" color="primary" disabled={isStarting}>
              {ALEGRA_SYNC_TEXTS.confirmButton}
            </Button>
          </>
        )}
        {(isRunning || isFinished) && (
          <Button onClick={onClose} variant={isFinished ? "contained" : "text"}>
            {ALEGRA_SYNC_TEXTS.closeButton}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AlegraSyncDialog;
